import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import Accordion from '../../../shared/Accordion';
import SaveButton from '../../../shared/SaveButton';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import { formatMoney } from '../../../shared/numberFormat';
import { askConfirm } from '../../../shared/AppDialogs';

const SECONDARY_BUTTON = { padding: '7px 14px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 };
const LABEL = { fontSize: 13, display: 'block', marginBottom: 4 };
const HELP = { fontSize: 12, color: '#888', marginTop: 4 };
const CELL = { padding: '6px 8px', borderBottom: '1px solid #e5e9f0', textAlign: 'left' };

const PARCEL_FIELDS = [
  { key: 'postcode', label: 'Destination postcode', width: 150 },
  { key: 'weight', label: 'Weight (kg)', width: 100 },
  { key: 'length', label: 'Length (cm)', width: 100 },
  { key: 'width', label: 'Width (cm)', width: 100 },
  { key: 'height', label: 'Height (cm)', width: 100 },
];

function zoneLabel(result) {
  return `${result.from_postcode} (${result.from_area}) → ${result.to_postcode} (${result.to_area}) · Zone ${result.zone}`;
}

// Platform > API Integrations' Australia Post Postage Assessment Calculator
// (PAC) API card (issue #315) — Admin only, the key alone. PAC gives
// Australia Post's public prices; Optim takes off the MyPost Business
// saving for the band set on Sales > Australia Post (issue #328) and the
// parcel's zone (backend/services/australiaPostService.js).
function AustraliaPostCard() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [parcel, setParcel] = useState({ postcode: '', weight: '', length: '', width: '', height: '' });
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkAllResult, setCheckAllResult] = useState(null);

  const applySettings = (data) => setSettings(data);

  useEffect(() => {
    axios.get('/api/australia-post')
      .then((res) => applySettings(res.data.data))
      .catch((err) => showError(err, 'Failed to load the PAC API settings'));
    // eslint-disable-next-line
  }, []);

  const configured = !!settings?.configured;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put('/api/australia-post', { api_key: apiKey.trim() });
      applySettings(res.data.data);
      setApiKey('');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    if (!(await askConfirm('Remove the PAC API key?\n\nThe PAC API method can’t price shipping until a key is saved again.', { confirmLabel: 'Remove', destructive: true }))) return;
    setRemoving(true);
    try {
      const res = await axios.delete('/api/australia-post/key');
      applySettings(res.data.data);
      setResult(null);
      dismissError();
    } catch (err) {
      showError(err, 'Remove failed');
    }
    setRemoving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post('/api/australia-post/test', parcel);
      applySettings(res.data.data);
      setResult(res.data.result);
      dismissError();
    } catch (err) {
      showError(err, 'Test failed');
    }
    setTesting(false);
  };

  // Check All (#158): every Australian Address showing Not checked.
  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const res = await axios.post('/api/australia-post/check-addresses');
      setCheckAllResult(res.data.result);
      dismissError();
    } catch (err) {
      showError(err, 'Check failed');
    }
    setCheckingAll(false);
  };

  const title = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      Australia Post — Postage Assessment Calculator (PAC) API
      {configured ? (
        <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">Configured</Pill>
      ) : (
        <Pill color="#5a6b85" background="#dde3ea">Not configured</Pill>
      )}
    </span>
  );

  if (!settings) return null;

  return (
    <Accordion title={title} defaultOpen={false}>
      <div style={{ fontSize: 13, color: '#666', marginTop: -6, marginBottom: 12, maxWidth: 480 }}>
        Looks up Australia Post domestic parcel prices from the business address on Company Info. PAC returns Australia Post’s public prices; Optim takes off your MyPost Business saving for the savings band on Sales › Australia Post, where the PAC API method is switched on.
      </div>

      <label style={LABEL}>API Key</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="styled-input"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={configured ? '•••••••••••••• (configured — enter a new api key to replace it)' : 'Paste api key'}
          style={{ flex: 1 }}
          autoComplete="off"
        />
        {configured && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            title="Remove the saved PAC API key"
            style={{ ...SECONDARY_BUTTON, cursor: removing ? 'not-allowed' : 'pointer' }}
          >
            Remove
          </button>
        )}
      </div>
      <div style={HELP}>A Postage Assessment Calculator (PAC) key from the Australia Post Developer Centre.</div>

      <div style={{ marginTop: 16 }}>
        <SaveButton type="button" onClick={handleSave} loading={saving} />
      </div>

      <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #e5e9f0' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Test Connection</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {PARCEL_FIELDS.map((f) => (
            <div key={f.key}>
              <label style={LABEL}>{f.label}</label>
              <input
                className="styled-input"
                inputMode="decimal"
                value={parcel[f.key]}
                onChange={(e) => setParcel((prev) => ({ ...prev, [f.key]: e.target.value }))}
                style={{ width: f.width }}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !configured}
            title={!configured ? 'Save an API key first' : 'Price this parcel from the business address'}
            style={{ ...SECONDARY_BUTTON, cursor: (testing || !configured) ? 'not-allowed' : 'pointer' }}
          >
            {testing && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {settings.last_tested_at && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            {settings.last_test_ok ? <CheckCircle2 size={15} color="#1a7f4b" /> : <XCircle size={15} color="#b91c1c" />}
            <span style={{ color: settings.last_test_ok ? '#1a7f4b' : '#b91c1c' }}>{settings.last_test_message}</span>
            <span style={{ color: '#999' }}>· {formatDateTime(settings.last_tested_at)}</span>
          </div>
        )}

        {result?.ok && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 13, color: '#555' }}>
              {zoneLabel(result)} · Band {result.band} saves {result.saving_percent}%{result.rate_card ? ` (${result.rate_card})` : ' (no Rate Card in force)'} · Prices include GST
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
              <thead>
                <tr style={{ color: '#666' }}>
                  <th style={CELL}>Service</th>
                  <th style={CELL}>Packaging</th>
                  <th style={{ ...CELL, textAlign: 'right' }}>Standard</th>
                  <th style={{ ...CELL, textAlign: 'right' }}>Your price</th>
                </tr>
              </thead>
              <tbody>
                {result.services.map((s) => (
                  <tr key={s.code}>
                    <td style={CELL}>{s.name}</td>
                    <td style={CELL}>{s.size ? `${s.packaging} — ${s.size}` : s.packaging}</td>
                    <td style={{ ...CELL, textAlign: 'right', color: '#888' }}>{formatMoney(s.retail_price)}</td>
                    <td style={{ ...CELL, textAlign: 'right', fontWeight: 600 }}>{formatMoney(s.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #e5e9f0' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Australian Addresses</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8, maxWidth: 480 }}>
          Every Australian Suburb, State and Postcode is checked with Australia Post’s Postcode Search, on this key, when it’s saved. Addresses saved before a key was added, or while Australia Post couldn’t be reached, show Not checked.
        </div>
        <button
          type="button"
          onClick={handleCheckAll}
          disabled={checkingAll || !configured}
          title={!configured ? 'Save an API key first' : 'Check every Not checked Australian Address with Australia Post'}
          style={{ ...SECONDARY_BUTTON, cursor: (checkingAll || !configured) ? 'not-allowed' : 'pointer' }}
        >
          {checkingAll && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
          {checkingAll ? 'Checking...' : 'Check All'}
        </button>
        {checkAllResult && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#555' }}>
            {checkAllResult.matched} checked · {checkAllResult.mismatch} Postcode doesn’t match · {checkAllResult.not_checked} still Not checked
            {checkAllResult.stopped && <div style={{ color: '#b91c1c', marginTop: 4 }}>Stopped: {checkAllResult.stopped}</div>}
          </div>
        )}
      </div>
    </Accordion>
  );
}

export default AustraliaPostCard;
